import type { ReactNode } from "react";
import { AlertTriangle, Loader2, Store } from "lucide-react";

import { PRO014_STORE_MODE } from "@/@types/campaign";
import type { IPro014CampaignDetail } from "@/@types/campaign";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetPro014CampaignByCodeQuery } from "@/services/campaign-pro014";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { StoreSelector } from "../../create/components/shared/store-selector";
import { usePro014StoresEditor } from "../../hooks/use-pro014-stores-editor";
import type { Pro014StoresEditorState } from "../../types";
import { COMMON_TEXTS } from "../../utils/messages";
import { pro014StoresVersion } from "../../utils/pro014-stores-diff";
import { CampaignConflictModal } from "../campaign-conflict-modal";
import { CAMPAIGN_STATUS, PRO014_STORES_TEXTS } from "./constants";
import { Pro014StoreWindows } from "./pro014-store-windows";
import { Pro014StoresPreview } from "./pro014-stores-preview";

const TITLE_ID = "pro014-stores-title";
const CONFIRM_TITLE_ID = "pro014-stores-confirm-title";

interface Pro014StoresEditorProps {
  campaignCode: number;
  renderFooter?: (hasPendingChanges: boolean) => ReactNode;
  onPendingChange?: (pending: boolean) => void;
}

interface Pro014StoresEditorFormProps {
  campaignCode: number;
  detail: IPro014CampaignDetail;
  renderFooter?: (hasPendingChanges: boolean) => ReactNode;
  onPendingChange?: (pending: boolean) => void;
}

interface ConfirmAllStoresProps {
  editor: Pro014StoresEditorState;
}

function ConfirmAllStores({ editor }: ConfirmAllStoresProps): JSX.Element {
  return (
    <div
      role="group"
      aria-labelledby={CONFIRM_TITLE_ID}
      className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20"
    >
      <p id={CONFIRM_TITLE_ID} className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        {PRO014_STORES_TEXTS.CONFIRM_ALL_TITLE}
      </p>
      <p className="text-xs text-amber-900 dark:text-amber-300">
        {PRO014_STORES_TEXTS.CONFIRM_ALL_DESCRIPTION}
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={editor.cancelSwitchToAll}>
          {PRO014_STORES_TEXTS.BACK}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void editor.confirmSwitchToAll()}
          disabled={editor.isSaving}
        >
          {PRO014_STORES_TEXTS.CONFIRM}
        </Button>
      </div>
    </div>
  );
}

function Pro014StoresEditorForm({
  campaignCode,
  detail,
  renderFooter,
  onPendingChange,
}: Pro014StoresEditorFormProps): JSX.Element {
  const editor: Pro014StoresEditorState = usePro014StoresEditor(
    campaignCode,
    detail,
    onPendingChange,
  );
  const isLegacy: boolean = detail.storeMode === PRO014_STORE_MODE.LEGACY;

  return (
    <>
      <section className="space-y-3 rounded-lg border p-3" aria-labelledby={TITLE_ID}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id={TITLE_ID} className="flex items-center gap-2 text-sm font-medium">
            <Store className="h-4 w-4" aria-hidden />
            {PRO014_STORES_TEXTS.TITLE}
          </h3>
          {editor.isQueued && (
            <Badge variant="secondary" className="text-xs">
              {PRO014_STORES_TEXTS.PROCESSING}
            </Badge>
          )}
          {!editor.isQueued && editor.preview.hasChanges && (
            <Badge variant="secondary" className="text-xs">
              {PRO014_STORES_TEXTS.PENDING_CHANGES}
            </Badge>
          )}
        </div>

        {isLegacy && <p className="text-xs text-muted-foreground">{PRO014_STORES_TEXTS.LEGACY_NOTICE}</p>}

        <Pro014StoreWindows detail={detail} />

        <StoreSelector
          allStores={editor.selection.allStores}
          selectedStores={editor.selection.stores}
          updateData={editor.updateSelection}
          error={editor.error}
          clearError={editor.clearError}
          hideAllStoresOption={isLegacy}
        />

        <Pro014StoresPreview preview={editor.preview} />

        {editor.isConfirmingAll && <ConfirmAllStores editor={editor} />}

        {!editor.isConfirmingAll && (
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={() => void editor.requestSave()}
              disabled={editor.isSaving || editor.isQueued || !editor.preview.hasChanges}
            >
              {editor.isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              {PRO014_STORES_TEXTS.SAVE}
            </Button>
          </div>
        )}

        <CampaignConflictModal
          isOpen={editor.conflicts.length > 0}
          conflicts={editor.conflicts}
          onClose={editor.closeConflicts}
          onGoBack={editor.closeConflicts}
        />
      </section>
      {renderFooter?.(editor.preview.hasChanges)}
    </>
  );
}

export function Pro014StoresEditor({
  campaignCode,
  renderFooter,
  onPendingChange,
}: Pro014StoresEditorProps): JSX.Element | null {
  const { data: detail, isError, error } = useGetPro014CampaignByCodeQuery(campaignCode);

  if (detail && detail.status === CAMPAIGN_STATUS.EXPIRED) return <>{renderFooter?.(false)}</>;

  if (detail) {
    return (
      <Pro014StoresEditorForm
        key={pro014StoresVersion(detail)}
        campaignCode={campaignCode}
        detail={detail}
        renderFooter={renderFooter}
        onPendingChange={onPendingChange}
      />
    );
  }

  if (isError) {
    return (
      <>
        <p className="text-sm text-destructive">
          {extractRtkErrorMessage(error, COMMON_TEXTS.CAMPAIGN_LOAD_FAILED)}
        </p>
        {renderFooter?.(false)}
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
      </div>
      {renderFooter?.(false)}
    </>
  );
}
